import { createContext, createElement, useContext, useEffect, useState, type ReactNode } from 'react';

// ───────────────────────────────────────────────────────────────────────────
// Paleta de cores — tokens semânticos.
// DARK = valores históricos (não mudam). LIGHT = proposta validada (fundo claro,
// tudo claro inclusive a sidebar). Ambas em HEX real: o código anexa alpha ao
// hex (ex. `${C.warning}44`), então as cores NÃO podem virar CSS var.
//
// `C` é um objeto com getters que leem a paleta ATIVA. Componentes seguem usando
// `import { C }` + `C.x` sem mudança; o toggle troca a paleta ativa e remonta a
// árvore (ThemeProvider), então todo `C.x` relido passa a devolver o modo atual.
// ───────────────────────────────────────────────────────────────────────────

export type ThemeMode = 'dark' | 'light';

export type Palette = {
  bg: string; card: string; deep: string; border: string;
  white: string; text: string; soft: string; muted: string; dim: string;
  success: string; warning: string; danger: string; info: string;
  gold: string; accent: string;
  goldText: string;    // dourado p/ TEXTO (gold puro é só p/ fundo de botão/borda)
  // Superfícies/semânticos extras (migram hex literais p/ ficarem theme-aware).
  // No DARK, cada um = valor literal atual (sem mudança visual no escuro).
  inputBg: string;     // fundo de inputs (#0d1e2e & variantes)
  activeBg: string;    // fundo de item selecionado/ativo (#1a3a6a)
  activeInk: string;   // texto sobre activeBg (#eef4fc)
  borderStrong: string;// borda/hover mais forte (#2a3a4a)
  onGold: string;      // texto escuro sobre dourado (#0b1121)
  redSoft: string;     // vermelho claro (#e05050) — só no escuro; escurece no claro
  greenSoft: string;   // verde claro (#6db87a)
  blueSoft: string;    // azul claro (#7ab0e0)
  amber: string;       // âmbar/dourado dessaturado (#d4a060)
};

export const DARK: Palette = {
  // Backgrounds — verde-escuro neutro, sem azul dominante
  bg:     '#0d1117',   // fundo base (quase preto)
  card:   '#161f2e',   // cards e painéis
  deep:   '#0f1a27',   // inputs, áreas internas
  // Borders
  border: '#263548',   // bordas sutis
  // Text — hierarquia clara, todos com contraste ≥4.5:1 sobre card
  white:  '#f0f4f8',   // títulos e valores principais  (15:1 sobre card)
  text:   '#ccd8e4',   // texto padrão                  (11:1 sobre card)
  soft:   '#94aabf',   // labels e secundário            (6.9:1 sobre card)
  muted:  '#728a9e',   // placeholders e terciário       (4.2:1 sobre card)
  dim:    '#3d5268',   // muito apagado — decorativo, nunca texto
  // Semantic — status
  success: '#4caf72',  // OK, positivo
  warning: '#e0a030',  // atenção, FIN
  danger:  '#e07676',  // erro, OVER
  info:    '#5d9cd5',  // informativo, OPS
  // Accent — dourado GRI real
  gold:    '#c9a84c',  // ação principal, botões, destaques
  accent:  '#4caf72',  // variante positiva
  goldText: '#c9a84c', // 5.7:1 no pior fundo
  // Extras — DARK = valores literais atuais (sem mudança visual no escuro)
  inputBg:      '#0d1e2e',
  activeBg:     '#1a3a6a',
  activeInk:    '#eef4fc',
  borderStrong: '#2a3a4a',
  onGold:       '#0b1121',
  redSoft:      '#e67373',
  greenSoft:    '#6db87a',
  blueSoft:     '#7ab0e0',
  amber:        '#d4a060',
};

export const LIGHT: Palette = {
  // Fundo claro — tudo claro, inclusive a sidebar (card = branco)
  bg:     '#f3f5f8',
  card:   '#ffffff',
  deep:   '#eef2f6',
  border: '#d6dee7',
  // Texto — todos ≥4.5:1 sobre card
  white:  '#081722',   // navy GRI — títulos e valores  (18.2:1)
  text:   '#1f3247',   // texto padrão                  (13.1:1)
  soft:   '#475b70',   // labels e nav                   (7.0:1)
  muted:  '#5d6e81',   // secundário / placeholder       (4.7:1)
  dim:    '#c3cdd8',   // decorativo
  // Semantic — tons mais escuros p/ legibilidade no branco
  success: '#1c7741',
  warning: '#925d00',
  danger:  '#b8322a',
  info:    '#1d65a8',
  // Accent — dourado mantido (botões)
  gold:    '#c9a84c',   // só fundo de botão/borda — texto sobre ele = onGold (navy, 7.9:1)
  goldText: '#7d6626',  // dourado escuro p/ texto (4.6:1 no pior fundo)
  accent:  '#1c7741',
  // Extras — valores p/ fundo claro (contraste ≥4.5:1 sobre card branco)
  inputBg:      '#eef2f6',
  activeBg:     '#e6f0fa',   // tint azul claro
  activeInk:    '#081722',   // navy sobre o tint
  borderStrong: '#c7d2de',
  onGold:       '#081722',   // navy sobre dourado
  redSoft:      '#b8322a',
  greenSoft:    '#1c7741',
  blueSoft:     '#1d65a8',
  amber:        '#82641d',
};

export const PALETTES: Record<ThemeMode, Palette> = { dark: DARK, light: LIGHT };

// Paleta ativa (mutável). ThemeProvider a ajusta no render antes dos filhos.
let active: Palette = DARK;

// `C` com getters enumeráveis → `C.x` sempre devolve a paleta ativa, e
// Object.values(C)/spread continuam funcionando (usados em testes).
export const C: Palette = Object.defineProperties(
  {} as Palette,
  Object.fromEntries(
    (Object.keys(DARK) as (keyof Palette)[]).map((k) => [
      k,
      { enumerable: true, get: () => active[k] },
    ]),
  ),
);

// ───────────────────────────────────────────────────────────────────────────
// Contexto de tema
// ───────────────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'oraculo-theme';

type ThemeCtx = { mode: ThemeMode; setMode: (m: ThemeMode) => void; toggle: () => void };
const ThemeContext = createContext<ThemeCtx>({ mode: 'dark', setMode: () => {}, toggle: () => {} });

function readInitialMode(): ThemeMode {
  if (typeof window === 'undefined') return 'dark';
  const saved = window.localStorage.getItem(STORAGE_KEY);
  return saved === 'light' ? 'light' : 'dark'; // default ESCURO
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(readInitialMode);

  // ajusta a paleta ativa ANTES dos filhos renderizarem
  active = PALETTES[mode];

  const setMode = (m: ThemeMode) => {
    setModeState(m);
    try { window.localStorage.setItem(STORAGE_KEY, m); } catch { /* ignore */ }
  };
  const toggle = () => setMode(mode === 'dark' ? 'light' : 'dark');

  useEffect(() => {
    // expõe o modo no <html> p/ o CSS global (body, scrollbar, login) acompanhar
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode]);

  // `key={mode}` remonta a árvore ao trocar → todo estilo inline relê a paleta ativa
  return createElement(
    ThemeContext.Provider,
    { value: { mode, setMode, toggle } },
    createElement('div', { key: mode, style: { display: 'contents' } }, children),
  );
}

/** Modo + controles (p/ o botão de toggle). */
export function useTheme(): ThemeCtx {
  return useContext(ThemeContext);
}
